import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { useTranslation } from 'react-i18next';

// ── Tailwind class maps (migrated from style/Growl.css) ──────────────
// BEM marker classes (`rw-growl*`) are preserved as inert tags so the
// existing test suite (Growl.test.tsx) — which asserts on
// `rw-growl-wrapper__trigger`, `rw-growl__title--bold`,
// `rw-growl--error`, `rw-growl__progress-fill`, etc. — continues to pass.

const WRAPPER_CLASS = 'rw-growl-wrapper inline-block';

const TRIGGER_CLASS = [
  'rw-growl-wrapper__trigger',
  'appearance-none bg-transparent border-0 p-0 m-0 font-[inherit] text-[inherit]',
  'inline-flex items-center cursor-pointer',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-accent)] focus-visible:outline-offset-2 focus-visible:rounded-hk-sm',
].join(' ');

// Fixed top-center stack — matches Vue's naive-ui `<n-message-provider>`
// default placement ('top'), which the app relies on with no override.
// `pointer-events-none` so the gap between notifications doesn't block
// clicks; each card opts back in via `pointer-events-auto`. Mobile
// (≤640px) anchors edge-to-edge instead of staying a fixed-width column.
const HOST_CLASS = [
  'rw-growl-host fixed top-4 left-1/2 -translate-x-1/2 z-[9999]',
  'flex flex-col gap-3 pointer-events-none items-center',
  'max-w-[min(360px,calc(100vw-32px))]',
  'max-[640px]:top-2 max-[640px]:left-2 max-[640px]:right-2 max-[640px]:translate-x-0 max-[640px]:max-w-none',
].join(' ');

// One notification card. `animate-growl-enter` plays the 180ms fade-in
// (defined in tailwind.config.js). `[box-shadow]` arbitrary syntax
// preserves the original `0 6px 20px rgba(0,0,0,0.18)` drop shadow.
const CARD_BASE_CLASS = [
  'rw-growl pointer-events-auto flex flex-col',
  'bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]',
  'border border-[var(--color-border)] rounded-md',
  'shadow-[0_6px_20px_rgba(0,0,0,0.18)] overflow-hidden',
  'animate-growl-enter',
].join(' ');

// Per-type marker — kept as a static class so it remains visible to test
// queries. Status-driven tints below target `[.rw-growl--success_&]:`
// etc via Tailwind's arbitrary descendant variant.
const CARD_TYPE_CLASS: Record<'success' | 'warning' | 'error', string> = {
  success: 'rw-growl--success',
  warning: 'rw-growl--warning',
  error: 'rw-growl--error',
};

const BODY_CLASS = 'rw-growl__body flex items-start gap-2.5 px-3.5 py-3';

// Icon — tinted by the parent card's type modifier via descendant variants.
const ICON_CLASS = [
  'rw-growl__icon inline-flex flex-none mt-px',
  '[.rw-growl--success_&]:text-[var(--color-accent)]',
  '[.rw-growl--warning_&]:text-[#d97706]',
  '[.rw-growl--error_&]:text-[var(--color-danger)]',
].join(' ');

const TEXT_WRAP_CLASS = 'rw-growl__text flex-auto min-w-0';

const TITLE_BASE_CLASS =
  'rw-growl__title text-sm leading-[1.3] text-[var(--color-text-primary)] break-words';

const TITLE_BOLD_MOD_CLASS = 'rw-growl__title--bold font-hk-semibold';

const DESCRIPTION_CLASS =
  'rw-growl__description mt-1 text-[13px] leading-[1.4] text-[var(--color-text-secondary)] break-words';

const CLOSE_CLASS = [
  'rw-growl__close appearance-none bg-transparent border-0',
  'p-1 -mt-0.5 -mr-1 flex-none inline-flex items-center justify-center',
  'text-[var(--color-text-muted)] cursor-pointer rounded-hk-sm',
  'hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)]',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-accent)] focus-visible:outline-offset-[1px]',
].join(' ');

const PROGRESS_TRACK_CLASS =
  'rw-growl__progress h-[3px] bg-[var(--color-bg-secondary)]';

// Progress fill — tinted by the parent card's type modifier.
const PROGRESS_FILL_CLASS = [
  'rw-growl__progress-fill h-full w-0 transition-[width] duration-100 ease-linear',
  '[.rw-growl--success_&]:bg-[var(--color-accent)]',
  '[.rw-growl--warning_&]:bg-[#d97706]',
  '[.rw-growl--error_&]:bg-[var(--color-danger)]',
].join(' ');

/**
 * Generic Growl — port of RW-ERP-Service `components/common/library/HkGrowl.vue`.
 *
 * The Vue component wraps a trigger button that, on click, fires a naive-ui
 * notification with an icon, optional bold title (when a description is
 * present), an optional description body, a status-colored progress bar that
 * fills over `seconds`, and a configurable close behavior. This React port
 * keeps the same shape but doesn't depend on a third-party notification host:
 *
 *   • `<Growl>` renders the trigger and (on click / on `immediate`) calls
 *     `showGrowl(...)` to push a notification onto the shared host.
 *   • `useGrowl()` returns the same imperative function from any consumer
 *     without rendering a button.
 *   • `showGrowl({...})` is the bare imperative API; safe to call from event
 *     handlers, routers, react-query callbacks, etc.
 *
 * The host is a single fixed-position container portaled to `document.body`
 * the first time a notification is shown. It auto-cleans when the queue is
 * empty so repeated server-rendered tests stay isolated.
 */

export type GrowlType = 'success' | 'warning' | 'error';

export const GROWL_TYPE = {
  Success: 'success' as const,
  Warning: 'warning' as const,
  Error: 'error' as const,
};

export type GrowlOptions = {
  /** The bold title text (or a fallback if `displayTitleLabelName` is set). */
  title: string;
  /** Optional description body shown under the title. */
  description?: string;
  /** i18n key for the title; falls back to `title` when no translation exists. */
  displayTitleLabelName?: string | null;
  /** i18n key for the description; falls back to `description`. */
  displayDescriptionLabelName?: string | null;
  /** Seconds the notification stays visible. Default 3. */
  seconds?: number;
  /** Status: 'success' | 'warning' | 'error'. Default 'success'. */
  type?: GrowlType;
  /**
   * When true (default), the notification auto-closes after `seconds` and
   * does NOT render a close button. When false, the notification stays open
   * until the user clicks the close button.
   *
   * NOTE: this matches the Vue HkGrowl semantics — autoClose=false enables
   * the close button (`closable: props.autoClose === false`).
   */
  autoClose?: boolean;
};

export type GrowlProps = GrowlOptions & {
  /** When true, the notification fires on mount and any time the prop flips to true. */
  immediate?: boolean;
  /** When false, the trigger button is not rendered (use only `immediate` or external triggers). */
  showTrigger?: boolean;
  /** Trigger button content. Equivalent to Vue's `<template #trigger>` slot. */
  children?: React.ReactNode;
  /** Class on the outer wrapper. */
  className?: string;
  /** Class on the trigger button itself. */
  triggerClassName?: string;
};

type ActiveNotification = GrowlOptions & {
  id: number;
  /** Resolved (translated) title for rendering. */
  resolvedTitle: string;
  /** Resolved (translated) description, empty when none. */
  resolvedDescription: string;
};

type HostListener = (queue: ReadonlyArray<ActiveNotification>) => void;

/* ---------------- Singleton notification host ---------------- */

const HOST_ROOT_ID = 'rw-growl-host-root';

let hostQueue: ActiveNotification[] = [];
const hostListeners = new Set<HostListener>();
let nextId = 1;
let hostRoot: Root | null = null;
let hostElement: HTMLElement | null = null;

const notifyListeners = (): void => {
  const snapshot = hostQueue.slice();
  hostListeners.forEach((listener) => listener(snapshot));
};

const removeNotification = (id: number): void => {
  const next = hostQueue.filter((n) => n.id !== id);
  if (next.length === hostQueue.length) return;
  hostQueue = next;
  notifyListeners();
};

/*
  Mount the singleton notification host as a vanilla React root attached to
  document.body. We deliberately DON'T render it through the consumer's
  component tree — that way the host remains a single instance regardless of
  how many <Growl> wrappers are present in the page.
*/
const ensureHostMounted = (): void => {
  if (typeof document === 'undefined') return;
  if (hostRoot !== null) return;
  let el = document.getElementById(HOST_ROOT_ID);
  if (el === null) {
    el = document.createElement('div');
    el.id = HOST_ROOT_ID;
    document.body.appendChild(el);
  }
  hostElement = el;
  hostRoot = createRoot(el);
  hostRoot.render(<NotificationHost />);
};

const SECONDS_DEFAULT = 3;

/**
 * Imperative API — show a single growl notification. Returns a `close`
 * function so callers can dismiss it programmatically (e.g. from a parent
 * effect cleanup, a websocket replacement event, etc.).
 *
 * Translation is intentionally NOT performed here — this layer is i18n-free
 * so it can be called from any non-React context. The `<Growl>` wrapper and
 * `useGrowl()` hook resolve translation up-front and pass the pre-translated
 * `title` / `description` through.
 */
/**
 * Test-only helper — clears the singleton notification queue. NOT exported
 * from the barrel; only the test file imports it directly. Production code
 * has no reason to reach into the host state.
 */
export const __resetGrowlForTests = (): void => {
  hostQueue = [];
  nextId = 1;
  notifyListeners();
  if (hostRoot !== null) {
    hostRoot.unmount();
    hostRoot = null;
  }
  if (hostElement?.parentNode != null) {
    hostElement.parentNode.removeChild(hostElement);
  }
  hostElement = null;
};

export const showGrowl = (options: GrowlOptions): { id: number; close: () => void } => {
  ensureHostMounted();
  const id = nextId++;
  const next: ActiveNotification = {
    ...options,
    id,
    resolvedTitle: options.title,
    resolvedDescription: options.description ?? '',
  };
  hostQueue = [...hostQueue, next];
  notifyListeners();
  return {
    id,
    close: () => removeNotification(id),
  };
};

/**
 * Hook that translates `displayTitleLabelName` / `displayDescriptionLabelName`
 * before delegating to `showGrowl`. Mirrors the Vue `translateText(key, fallback)`
 * helper: when the key is absent or its translation equals the key (i.e. no
 * resource entry), the literal `title` / `description` is used instead.
 */
export const useGrowl = (): ((options: GrowlOptions) => { id: number; close: () => void }) => {
  const { t, i18n } = useTranslation();
  return useCallback(
    (options: GrowlOptions) => {
      const resolvedTitle = resolveTranslation(
        t,
        i18n,
        options.displayTitleLabelName,
        options.title,
      );
      const resolvedDescription = resolveTranslation(
        t,
        i18n,
        options.displayDescriptionLabelName,
        options.description ?? '',
      );
      return showGrowl({
        ...options,
        title: resolvedTitle,
        description: resolvedDescription,
      });
    },
    [t, i18n],
  );
};

const resolveTranslation = (
  t: (key: string) => string,
  i18n: { exists?: (key: string) => boolean },
  key: string | null | undefined,
  fallback: string,
): string => {
  if (key === null || key === undefined || key.length === 0) return fallback;
  if (typeof i18n.exists === 'function' && !i18n.exists(key)) return fallback;
  const translated = t(key);
  // i18next returns the key untouched when no resource is found; treat that
  // as "no translation" so the literal fallback wins.
  if (translated === key) return fallback;
  return translated;
};

/* ---------------- Host renderer (the fixed stack) ---------------- */

const NotificationHost: React.FC = () => {
  const [queue, setQueue] = useState<ReadonlyArray<ActiveNotification>>(hostQueue);

  useEffect(() => {
    const listener: HostListener = (next) => setQueue(next);
    hostListeners.add(listener);
    setQueue(hostQueue);
    return () => {
      hostListeners.delete(listener);
    };
  }, []);

  if (queue.length === 0) return null;

  return (
    <section
      className={HOST_CLASS}
      aria-label="Notifications"
      aria-live="polite"
    >
      {queue.map((n) => (
        <NotificationItem
          key={n.id}
          notification={n}
          onClose={() => removeNotification(n.id)}
        />
      ))}
    </section>
  );
};

/* ---------------- Single notification item ---------------- */

const PROGRESS_TICK_MS = 100;

type NotificationItemProps = {
  notification: ActiveNotification;
  onClose: () => void;
};

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onClose }) => {
  const seconds = notification.seconds ?? SECONDS_DEFAULT;
  const type = notification.type ?? 'success';
  const autoClose = notification.autoClose ?? true;

  const totalDuration = Math.max(seconds * 1000, PROGRESS_TICK_MS);
  const [percentage, setPercentage] = useState<number>(0);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    setPercentage(0);
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const pct = Math.min((elapsed / totalDuration) * 100, 100);
      setPercentage(pct);
      if (pct >= 100) {
        window.clearInterval(interval);
        if (autoClose) onCloseRef.current();
      }
    }, PROGRESS_TICK_MS);
    return () => window.clearInterval(interval);
  }, [autoClose, totalDuration]);

  const hasDescription = notification.resolvedDescription.trim().length > 0;
  const titleClassName = hasDescription
    ? `${TITLE_BASE_CLASS} ${TITLE_BOLD_MOD_CLASS}`
    : TITLE_BASE_CLASS;

  return (
    <div
      className={`${CARD_BASE_CLASS} ${CARD_TYPE_CLASS[type]}`}
      role={type === 'error' ? 'alert' : undefined}
    >
      <div className={BODY_CLASS}>
        <span className={ICON_CLASS} aria-hidden="true">
          {renderTypeIcon(type)}
        </span>
        <div className={TEXT_WRAP_CLASS}>
          <div className={titleClassName}>{notification.resolvedTitle}</div>
          {hasDescription ? (
            <div className={DESCRIPTION_CLASS}>
              {notification.resolvedDescription}
            </div>
          ) : null}
        </div>
        {!autoClose ? (
          <button
            type="button"
            className={CLOSE_CLASS}
            aria-label="Close notification"
            onClick={onClose}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 6 18 18 M18 6 6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        ) : null}
      </div>
      <div className={PROGRESS_TRACK_CLASS} aria-hidden="true">
        <div
          className={PROGRESS_FILL_CLASS}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const renderTypeIcon = (type: GrowlType): React.ReactNode => {
  if (type === 'success') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" fill="currentColor" />
        <path
          d="M7 12.5 11 16.5 17 9.5"
          stroke="#fff"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (type === 'warning') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M12 2 1 21h22L12 2z" fill="currentColor" />
        <path
          d="M12 9v5"
          stroke="#fff"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle cx="12" cy="17" r="1" fill="#fff" />
      </svg>
    );
  }
  // error
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" fill="currentColor" />
      <path
        d="M8 8l8 8 M16 8l-8 8"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
};

/* ---------------- Public Growl wrapper component ---------------- */

const Growl: React.FC<GrowlProps> = ({
  title,
  description,
  displayTitleLabelName,
  displayDescriptionLabelName,
  seconds = SECONDS_DEFAULT,
  type = 'success',
  autoClose = true,
  immediate = false,
  showTrigger = true,
  children,
  className,
  triggerClassName,
}) => {
  const trigger = useGrowl();

  // Stable ref to the latest trigger so the immediate-effect doesn't refire
  // when caller-supplied closures change identity each render.
  const triggerRef = useRef(trigger);
  triggerRef.current = trigger;

  const optionsRef = useRef<GrowlOptions>({
    title,
    description,
    displayTitleLabelName,
    displayDescriptionLabelName,
    seconds,
    type,
    autoClose,
  });
  optionsRef.current = {
    title,
    description,
    displayTitleLabelName,
    displayDescriptionLabelName,
    seconds,
    type,
    autoClose,
  };

  /*
    Mirror Vue's `watch(() => props.immediate, …, { immediate: true })`:
    fire when `immediate` is true on mount, AND fire again any time it
    flips false → true.
  */
  useEffect(() => {
    if (immediate) {
      triggerRef.current(optionsRef.current);
    }
    // We deliberately list only `immediate` so toggling other props does NOT
    // refire the notification — the click handler is the canonical entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immediate]);

  const handleClick = useCallback(() => {
    triggerRef.current(optionsRef.current);
  }, []);

  const wrapperClassName = useMemo(() => {
    const parts = [WRAPPER_CLASS];
    if (className !== undefined) parts.push(className);
    return parts.join(' ');
  }, [className]);

  return (
    <span className={wrapperClassName}>
      {showTrigger ? (
        <button
          type="button"
          className={
            TRIGGER_CLASS +
            (triggerClassName !== undefined ? ` ${triggerClassName}` : '')
          }
          onClick={handleClick}
        >
          {children}
        </button>
      ) : null}
    </span>
  );
};

export default Growl;
